import { supabase } from '@/utils/supabase';

export interface UserSession {
  id: string;
  userId: string;
  userName: string;
  userRole: 'borrower' | 'loanOfficer' | 'admin';
  userEmail: string;
  organizationId: string;
  createdAt: string;
  lastActivityAt: string;
  expiresAt: string;
  ipAddress?: string;
  userAgent?: string;
  isActive: boolean;
  sessionToken: string;
}

// Remove localStorage storage for session tokens (security fix)
// Session tokens are now managed through Supabase auth system only
const _SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes (1 hour)
const ACTIVITY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes warning

export class SessionService {
  private static activityTimer: ReturnType<typeof setTimeout> | null = null;
  private static warningTimer: ReturnType<typeof setTimeout> | null = null;
  private static onExpireCallbacks: Array<() => void> = [];
  private static onWarningCallbacks: Array<(minutesLeft: number) => void> = [];
  private static lastActivityUpdate = 0;
  private static updateThrottle = 60000; // 1 minute

  /**
   * Create a new session
   */
  static async createSession(params: {
    userId: string;
    organizationId: string;
  }): Promise<UserSession | null> {
    try {
      // Get user details
      const { data: profile } = await supabase
        .from('users')
        .select('full_name, email, roles')
        .eq('id', params.userId)
        .single();

      if (!profile) {
return null;
}

      // Create session in database
      const { data: session, error } = await supabase
        .rpc('create_user_session', {
          p_user_id: params.userId,
          p_organization_id: params.organizationId,
          p_ip_address: null, // Would be set by server in production
          p_user_agent: navigator.userAgent,
          p_metadata: {}
        });

      if (error) {
        console.error('Failed to create session:', error);
        return null;
      }

      // Session tokens are now stored securely in Supabase auth system only
      // No longer storing sensitive tokens in localStorage

      // Start session monitoring
      this.startSessionMonitoring();

      return {
        id: session.id,
        userId: session.user_id,
        userName: profile.full_name,
        userEmail: profile.email,
        userRole: profile.roles?.[0] as 'borrower' | 'loanOfficer' | 'admin',
        organizationId: session.organization_id,
        createdAt: session.created_at,
        lastActivityAt: session.last_activity_at,
        expiresAt: session.expires_at,
        ipAddress: session.ip_address,
        userAgent: session.user_agent,
        isActive: session.is_active,
        sessionToken: session.session_token
      };
    } catch (error) {
      console.error('Session creation error:', error);
      return null;
    }
  }

  /**
   * Get current session - now uses Supabase auth system instead of localStorage tokens
   */
  static async getCurrentSession(): Promise<UserSession | null> {
    try {
      // Get current session from Supabase auth (secure storage)
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        return null;
      }

      // Get user profile data
      const { data: profile } = await supabase
        .from('users')
        .select('full_name, email, roles, organization_id')
        .eq('id', session.user.id)
        .single();

      if (!profile) {
        return null;
      }

      // Return session data based on Supabase auth session
      return {
        id: session.user.id,
        userId: session.user.id,
        userName: profile.full_name || session.user.email || 'Unknown User',
        userEmail: profile.email || session.user.email || '',
        userRole: profile.roles?.[0] as 'borrower' | 'loanOfficer' | 'admin',
        organizationId: profile.organization_id,
        createdAt: session.user.created_at,
        lastActivityAt: new Date().toISOString(),
        expiresAt: new Date(session.expires_at! * 1000).toISOString(),
        isActive: true,
        sessionToken: session.access_token, // Use Supabase access token instead
        ipAddress: undefined,
        userAgent: navigator.userAgent
      };
    } catch (error) {
      console.error('Session retrieval error:', error);
      return null;
    }
  }

  /**
   * Update session activity (throttled) - now uses Supabase auth system
   */
  static async updateActivity(): Promise<void> {
    const now = Date.now();
    
    // Throttle updates to reduce database calls
    if (now - this.lastActivityUpdate < this.updateThrottle) {
      return;
    }

    try {
      // Check if we have a valid Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
return;
}

      // Refresh the session to update activity
      await supabase.auth.refreshSession();

      this.lastActivityUpdate = now;
      
      // Restart monitoring
      this.startSessionMonitoring();
    } catch (error) {
      console.error('Failed to update session activity:', error);
    }
  }

  /**
   * End current session - now uses Supabase auth system
   */
  static async endSession(): Promise<void> {
    // Clear timers
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    try {
      // Sign out through Supabase auth system
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Failed to terminate session:', error);
    }

    // Notify callbacks
    this.onExpireCallbacks.forEach(callback => callback());
  }

  /**
   * Start monitoring session timeout
   */
  private static async startSessionMonitoring(): Promise<void> {
    const session = await this.getCurrentSession();
    if (!session) {
return;
}

    // Clear existing timers
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }

    const expiresAt = new Date(session.expiresAt);
    const now = new Date();
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();

    // Set warning timer (5 minutes before expiry)
    const warningTime = timeUntilExpiry - ACTIVITY_THRESHOLD_MS;
    if (warningTime > 0) {
      this.warningTimer = setTimeout(() => {
        const minutesLeft = Math.ceil(ACTIVITY_THRESHOLD_MS / 60000);
        this.onWarningCallbacks.forEach(callback => callback(minutesLeft));
      }, warningTime);
    }

    // Set expiry timer
    if (timeUntilExpiry > 0) {
      this.activityTimer = setTimeout(() => {
        this.endSession();
      }, timeUntilExpiry);
    }
  }


  /**
   * Subscribe to session expiry
   */
  static onSessionExpire(callback: () => void): () => void {
    this.onExpireCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.onExpireCallbacks.indexOf(callback);
      if (index > -1) {
        this.onExpireCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to session warning
   */
  static onSessionWarning(callback: (minutesLeft: number) => void): () => void {
    this.onWarningCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.onWarningCallbacks.indexOf(callback);
      if (index > -1) {
        this.onWarningCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Extend session
   */
  static async extendSession(): Promise<void> {
    await this.updateActivity();
  }

  /**
   * Check if user has permission
   */
  static async hasPermission(permission: string): Promise<boolean> {
    const session = await this.getCurrentSession();
    if (!session) {
return false;
}

    // Define role permissions
    const permissions: Record<string, string[]> = {
      admin: ['*'], // All permissions
      loanOfficer: [
        'pre-approval.create',
        'pre-approval.read',
        'pre-approval.update',
        'pre-approval.delete',
        'pre-approval.send',
        'template.create',
        'template.read',
        'template.update',
        'template.delete',
        'quote.create',
        'quote.read',
        'quote.update',
        'quote.delete',
        'quote.send',
        'contact.create',
        'contact.read',
        'contact.update',
        'contact.delete'
      ],
      borrower: [
        'pre-approval.read',
        'pre-approval.update:own',
        'pre-approval.download:own',
        'quote.read:own'
      ]
    };

    const userPermissions = permissions[session.userRole] || [];
    
    // Check for wildcard permission
    if (userPermissions.includes('*')) {
return true;
}
    
    // Check for exact permission
    if (userPermissions.includes(permission)) {
return true;
}
    
    // Check for partial permission (e.g., 'pre-approval.update:own' matches 'pre-approval.update')
    const permissionBase = permission.split(':')[0];
    return userPermissions.some(p => p.startsWith(permissionBase));
  }

  /**
   * Initialize session monitoring on app load
   */
  static async initialize(): Promise<void> {
    const session = await this.getCurrentSession();
    if (session) {
      this.startSessionMonitoring();
    }

    // Update activity on user interaction
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    let lastActivity = Date.now();
    
    const handleActivity = () => {
      const now = Date.now();
      // Only update if more than 1 minute has passed
      if (now - lastActivity > 60000) {
        lastActivity = now;
        this.updateActivity();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
  }

  /**
   * Get active sessions for organization (admin only)
   */
  static async getOrganizationSessions(organizationId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_organization_active_sessions', {
          p_organization_id: organizationId
        });

      if (error) {
        console.error('Failed to get organization sessions:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching organization sessions:', error);
      return [];
    }
  }
}